import { Outlet, useLocation, NavLink, Navigate } from "react-router-dom";
import { Home, Layers, Settings, Zap, type LucideIcon } from "lucide-react";
import { useAppContext } from "../store";
import { usePlatform, usePlatformBackButton } from "../lib/platformChrome";
import { AuthBridge } from "./AuthBridge";
import { useTranslation } from "react-i18next";

type NavEntry = {
  path: string;
  icon: LucideIcon;
  labelKey: string;
  end?: boolean;
};

// Five is what a thumb row fits. Anything role-specific (AI tutor, students)
// and anything secondary (profile, references) is reachable from Settings,
// which is the hub for exactly that.
//
// The ready-made sets are no longer among them: a set fills a dictionary, so
// it is reached from inside one rather than from a tab that looked like a
// second, parallel kind of content.
const TAB_ITEMS: NavEntry[] = [
  { path: "/", icon: Home, labelKey: "nav.home", end: true },
  { path: "/decks", icon: Layers, labelKey: "nav.decks" },
  { path: "/drill", icon: Zap, labelKey: "nav.drill" },
  { path: "/settings", icon: Settings, labelKey: "nav.settings" },
];

const TAB_PATHS = new Set(TAB_ITEMS.map((item) => item.path));

export function AppLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const { state } = useAppContext();
  const platform = usePlatform();

  // Use native host chrome where available (Telegram/MAX). VK and web keep
  // Lexibit's own navigation because their adapters have no native BackButton.
  // Not on the tabs themselves: they are siblings, so a back button there
  // repeats the tab bar and lands wherever the history happens to point.
  usePlatformBackButton(!TAB_PATHS.has(location.pathname));

  if (!state.settings.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }


  return (
    <div className="lexibit-shell flex h-[var(--host-viewport-stable-height)] min-h-0 flex-col bg-slate-50 font-sans text-slate-900 dark:bg-slate-950 dark:text-slate-100 lg:flex-row">
      <AuthBridge />

      <a
        href="#main-content"
        className="fixed left-3 top-3 z-50 -translate-y-24 rounded-lg bg-indigo-700 px-4 py-3 font-semibold text-white focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-white"
      >
        {t("nav.skipToContent")}
      </a>

      {/* The content owns the host's *content* safe area now that nothing
          sits above it: inside Telegram the close and menu buttons float
          over the top of the web view, and a page that starts at 0 runs
          underneath them. */}
      <main
        id="main-content"
        tabIndex={-1}
        className="order-1 min-h-0 flex-1 overflow-y-auto"
        style={{
          paddingTop: "var(--host-content-safe-area-inset-top)",
          paddingRight: "var(--host-content-safe-area-inset-right)",
          paddingLeft: "var(--host-content-safe-area-inset-left)",
        }}
      >
        <div className="lexibit-content mx-auto min-h-full max-w-6xl px-5 py-7 lg:px-10 lg:py-10">
          <Outlet />
        </div>
      </main>

      {platform.capabilities.showProductNavigation && (
        <nav
          aria-label={t("nav.menu")}
          className="lexibit-nav order-2 shrink-0 border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 lg:order-first lg:w-64 lg:border-r lg:border-t-0"
          style={{
            paddingBottom: "max(var(--host-content-safe-area-inset-bottom), var(--host-safe-area-inset-bottom))",
            paddingLeft: "var(--host-content-safe-area-inset-left)",
            paddingRight: "var(--host-content-safe-area-inset-right)",
          }}
        >
          <div className="mx-auto flex max-w-lg items-stretch lg:h-full lg:flex-col lg:gap-2 lg:px-5 lg:py-8">
          <LinkBrand />
          {TAB_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                aria-label={t(item.labelKey)}
                className={({ isActive }) =>
                  `flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-inset lg:flex-none lg:flex-row lg:justify-start lg:gap-3 lg:rounded-xl lg:px-4 ${
                    isActive
                      ? "lexibit-nav-active text-indigo-600 dark:text-indigo-300"
                      : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                  }`
                }
              >
                <Icon className="size-5 shrink-0" aria-hidden />
                <span className="max-w-full truncate text-xs leading-tight lg:text-sm">
                  {t(item.labelKey)}
                </span>
              </NavLink>
            );
          })}
          </div>
        </nav>
      )}
    </div>
  );
}

function LinkBrand() {
  return (
    <div className="mb-10 hidden items-center gap-3 px-3 text-xl font-semibold tracking-tight lg:flex">
      <img src="/telegram-logo.jpg" alt="" width={40} height={40} className="size-10 shrink-0 rounded-xl" />
      Lexibit
    </div>
  );
}
